import { useSuspenseQuery } from '@apollo/client/react';
import {
  Box,
  Spinner,
  Text,
  VStack,
  Button,
  HStack,
  Input,
} from '@chakra-ui/react';
import { Suspense, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import { DocumentCompare } from './document-compare';
import { DocumentHistory } from './document-history';
import { ViewDocument } from './view-document';

import { GET_DOCUMENT_BY_PATH } from '#src/graphql/queries/document';
import { useAppSettings } from '#src/providers/app-settings-provider';
import {
  isValidDocumentPath,
  DOCUMENT_PATH_DESCRIPTION,
} from '#src/utils/document-path';

/** Single document query; passes document + refetch to view/history/compare. Defined at module level so React keeps a stable component identity and avoids re-mount loops. */
function DocumentPageWithData({
  documentPath,
  isHistoryMode,
  isCompareMode,
}: {
  documentPath: string;
  isHistoryMode: boolean;
  isCompareMode: boolean;
}) {
  const { data, refetch: refetchDocument } = useSuspenseQuery(
    GET_DOCUMENT_BY_PATH,
    {
      variables: { path: documentPath },
      fetchPolicy: 'cache-first',
    },
  );
  const result = data?.documentByPath;
  const path =
    result?.__typename === 'Document'
      ? result.path
      : result?.__typename === 'DocumentNotFound'
        ? (result.requestedPath ?? documentPath)
        : documentPath;

  if (isCompareMode) {
    return (
      <DocumentCompare
        document={result?.__typename === 'Document' ? result : null}
        path={path}
        refetch={refetchDocument}
      />
    );
  }
  if (isHistoryMode) {
    return (
      <DocumentHistory
        document={result?.__typename === 'Document' ? result : null}
        path={path}
        refetch={refetchDocument}
      />
    );
  }
  return (
    <ViewDocument document={result} path={path} refetch={refetchDocument} />
  );
}

export function DocumentsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [createPath, setCreatePath] = useState('');
  const { documentPathRegex } = useAppSettings();

  // Parse the pathname to extract document path and mode
  // Path format: /documents/{documentPath}, /documents/{documentPath}/history, or /documents/{documentPath}/history/compare/{from}/{to}
  const pathMatch = location.pathname.match(/^\/documents\/(.+)$/);
  const fullPath = pathMatch ? pathMatch[1] : '';
  const isCompareMode = fullPath.includes('/history/compare/');
  const isHistoryMode = fullPath.endsWith('/history') && !isCompareMode;

  let documentPath: string | undefined;
  if (isCompareMode) {
    // Extract document path from /history/compare/{from}/{to}
    const compareMatch = fullPath.match(/^(.+)\/history\/compare\/.+/);
    documentPath = compareMatch ? compareMatch[1] : undefined;
  } else if (isHistoryMode) {
    documentPath = fullPath.slice(0, -'/history'.length);
  } else {
    documentPath = fullPath || undefined;
  }

  const trimmedCreatePath = createPath.trim();
  const isCreatePathValid = trimmedCreatePath
    ? isValidDocumentPath(trimmedCreatePath, documentPathRegex)
    : false;

  const handleCreateSubmit = () => {
    const path = createPath.trim();
    if (!path || !isValidDocumentPath(path, documentPathRegex)) return;
    setCreatePath('');
    navigate(`/documents/${path}`);
  };

  return (
    <Box>
      <VStack gap={4} align="stretch">
        {/* Document View/Edit/History/Compare */}
        <Box>
          {documentPath ? (
            <Suspense
              fallback={
                <Box
                  bg="white"
                  shadow="lg"
                  borderRadius="lg"
                  p={6}
                  height="100%"
                >
                  <Box
                    flex={1}
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                  >
                    <Spinner size="lg" />
                  </Box>
                </Box>
              }
            >
              <DocumentPageWithData
                documentPath={documentPath}
                isHistoryMode={isHistoryMode}
                isCompareMode={isCompareMode}
              />
            </Suspense>
          ) : (
            <Box
              bg="white"
              shadow="lg"
              borderRadius="lg"
              p={12}
              height="100%"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <VStack gap={4} width="100%" maxW="md" align="stretch">
                <Text fontSize="lg" color="gray.700" fontWeight="medium">
                  Create document
                </Text>
                <Text fontSize="sm" color="gray.500">
                  Enter a path to create a new document. You'll be taken to the
                  document page where you can create it.
                </Text>
                <VStack gap={2} align="stretch">
                  <Input
                    placeholder="e.g. folder/doc-name or path (with parens)"
                    value={createPath}
                    onChange={(e) => setCreatePath(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleCreateSubmit();
                    }}
                    aria-invalid={!!trimmedCreatePath && !isCreatePathValid}
                    borderColor={
                      trimmedCreatePath && !isCreatePathValid
                        ? 'red.500'
                        : undefined
                    }
                  />
                  {trimmedCreatePath && !isCreatePathValid && (
                    <Text fontSize="xs" color="red.500">
                      {DOCUMENT_PATH_DESCRIPTION}
                    </Text>
                  )}
                  <HStack gap={2}>
                    <Button
                      colorScheme="blue"
                      onClick={handleCreateSubmit}
                      disabled={!trimmedCreatePath || !isCreatePathValid}
                    >
                      Create
                    </Button>
                  </HStack>
                </VStack>
              </VStack>
            </Box>
          )}
        </Box>
      </VStack>
    </Box>
  );
}
